import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { formatDistanceToNow } from 'date-fns';
import { reviewsService } from '../../../services/reviewsService';

const ReviewsSection = ({ reviews, workerId, onReviewUpdated }) => {
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const handleSubmitResponse = async (ratingId) => {
    if (!responseText?.trim()) return;

    try {
      setSubmitting(true);
      setError(null);

      await reviewsService?.createReviewResponse({
        ratingId,
        response: responseText?.trim()
      });

      setResponseText('');
      setRespondingTo(null);
      onReviewUpdated?.();
    } catch (err) {
      console.error('Error submitting response:', err);
      setError(err?.message || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            className={`w-4 h-4 ${
              star <= rating
                ? 'text-warning fill-warning' :'text-muted-foreground'
            }`}
          />
        ))}
      </div>
    );
  };

  // Calculate review statistics
  const calculateStats = () => {
    if (!reviews || reviews?.length === 0) return null;

    const total = reviews?.length;
    const avgRating = reviews?.reduce((sum, r) => sum + (r?.rating || 0), 0) / total;
    
    const distribution = [5, 4, 3, 2, 1]?.map(star => {
      const count = reviews?.filter(r => r?.rating === star)?.length;
      const percentage = (count / total) * 100;
      return { star, count, percentage };
    });

    return { total, avgRating, distribution };
  };

  const stats = calculateStats();

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <h2 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
        <Icon name="MessageSquare" className="w-5 h-5 text-primary" />
        Client Reviews
      </h2>

      {/* Review Statistics */}
      {stats && (
        <div className="mb-8 p-6 bg-background rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Average Rating */}
            <div className="text-center md:text-left">
              <div className="text-4xl font-bold text-foreground mb-2">
                {stats?.avgRating?.toFixed(1)}
              </div>
              <div className="flex items-center justify-center md:justify-start gap-1 mb-2">
                {renderStars(Math.round(stats?.avgRating))}
              </div>
              <p className="text-sm text-muted-foreground">
                Based on {stats?.total} review{stats?.total !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Rating Distribution */}
            <div className="space-y-2">
              {stats?.distribution?.map(({ star, count, percentage }) => (
                <div key={star} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground w-8">
                    {star} <Icon name="Star" className="w-3 h-3 inline text-warning" />
                  </span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-warning transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-8 text-right">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Reviews List */}
      {!reviews || reviews?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="MessageSquare" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No reviews yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews?.map((review) => {
            const canRespond = workerId && review?.toUser?.id === workerId && !review?.reviewResponses?.[0];
            const hasResponse = review?.reviewResponses && review?.reviewResponses?.length > 0;

            return (
              <div key={review?.id} className="border border-border rounded-lg p-4">
                {/* Review Header */}
                <div className="flex items-start gap-4 mb-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {review?.fromUser?.avatarUrl ? (
                        <img
                          src={review?.fromUser?.avatarUrl}
                          alt={`${review?.fromUser?.fullName} avatar`}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <Icon name="User" className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-foreground">
                        {review?.fromUser?.fullName || 'Anonymous'}
                      </h4>
                      <span className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(review?.createdAt), { addSuffix: true })}
                      </span>
                    </div>

                    {renderStars(review?.rating)}

                    {review?.job?.title && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Job: {review?.job?.title}
                      </p>
                    )}
                  </div>
                </div>

                {/* Review Text */}
                {review?.review && (
                  <p className="text-foreground pl-14 mb-3">{review?.review}</p>
                )}

                {/* Review Photos */}
                {review?.photos && review?.photos?.length > 0 && (
                  <div className="pl-14 mb-3">
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                      {review?.photos?.map((photo, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedPhoto(photo)}
                          className="relative group cursor-pointer"
                        >
                          <img
                            src={photo?.url}
                            alt={photo?.name || `Review photo ${index + 1}`}
                            className="w-full h-20 object-cover rounded-md border border-border hover:border-primary transition-colors"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-md flex items-center justify-center">
                            <Icon name="ZoomIn" className="w-5 h-5 text-white" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Response Section */}
                {hasResponse && (
                  <div className="ml-14 mt-4 bg-background rounded-lg p-4 border-l-4 border-primary">
                    <div className="flex items-start gap-3">
                      <Icon name="CornerDownRight" className="w-4 h-4 text-primary mt-1" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground mb-1">
                          Response from worker
                        </p>
                        <p className="text-sm text-foreground">
                          {review?.reviewResponses?.[0]?.response}
                        </p>
                        <span className="text-xs text-muted-foreground mt-2 block">
                          {formatDistanceToNow(new Date(review?.reviewResponses?.[0]?.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Response Form */}
                {canRespond && (
                  <div className="ml-14 mt-4">
                    {respondingTo === review?.id ? (
                      <div className="space-y-3">
                        <textarea
                          value={responseText}
                          onChange={(e) => setResponseText(e?.target?.value)}
                          placeholder="Write your response..."
                          className="w-full min-h-[100px] px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                        />
                        {error && (
                          <p className="text-sm text-error flex items-center gap-1">
                            <Icon name="AlertCircle" className="w-4 h-4" />
                            {error}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleSubmitResponse(review?.id)}
                            disabled={submitting || !responseText?.trim()}
                            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                          >
                            {submitting && <Icon name="Loader2" className="w-4 h-4 animate-spin" />}
                            <span>Submit Response</span>
                          </button>
                          <button
                            onClick={() => {
                              setRespondingTo(null);
                              setResponseText('');
                              setError(null);
                            }}
                            disabled={submitting}
                            className="px-4 py-2 border border-border rounded-md hover:bg-accent disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setRespondingTo(review?.id)}
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        <Icon name="MessageSquare" className="w-4 h-4" />
                        <span>Respond</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Photo Lightbox Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <Icon name="X" className="w-6 h-6" />
            </button>
            <img
              src={selectedPhoto?.url}
              alt={selectedPhoto?.name || 'Review photo'}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e?.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;